import { labelForEvent } from "../../data/concepts/eventConceptMap";
import {
  getTeam,
  teamFullName,
  teamSpeakName,
  teamVerb,
} from "../../data/teams";
import { GAME_STATUS, isOffSchedule, winnerTeamId } from "../../utils/gameEngine";
import {
  normalizeDrives,
  normalizeGame,
  normalizeStatusType,
  normalizeTeamId,
} from "./espnNormalizer";

const SEASON_TYPE_LABELS = Object.freeze({
  1: "Preseason",
  2: "Regular Season",
  3: "Postseason",
});

const BRACKET_ROUND_TEST = /wild card|divisional|conference championship|super bowl/i;

const statValue = (stats = [], key) =>
  stats.find((stat) => stat?.name === key || stat?.type === key)?.displayValue ??
  null;

const numericStat = (stats = [], key) => {
  const found = stats.find((stat) => stat?.name === key || stat?.type === key);
  const value = Number(found?.value ?? found?.displayValue);
  return Number.isFinite(value) ? value : null;
};

const clincherDescription = (payload, symbol) =>
  symbol ? payload?.content?.config?.clinchers?.[symbol]?.description ?? null : null;

function playoffStatusFor({ seed, clincher }) {
  if (clincher === "*") {
    return {
      key: "bye",
      label: "Clinched bye",
      explanation:
        "They have the easiest starting path: they skip Wild Card weekend and wait for the next round.",
    };
  }

  if (clincher === "z") {
    return {
      key: "division",
      label: "Clinched division",
      explanation:
        "They won their division, so their playoff spot is locked in.",
    };
  }

  if (clincher === "y") {
    return {
      key: "wild-card",
      label: "Clinched wild card",
      explanation:
        "They did not win their division, but they still made the playoffs.",
    };
  }

  if (clincher === "e") {
    return {
      key: "eliminated",
      label: "Eliminated",
      explanation:
        "They cannot reach the playoffs anymore, even if they win out.",
    };
  }

  if (seed >= 1 && seed <= 4) {
    return {
      key: "division-leader",
      label: "Division leader",
      explanation:
        "If the season ended now, they would host a playoff game as a division winner.",
    };
  }

  if (seed >= 5 && seed <= 7) {
    return {
      key: "wild-card",
      label: "Wild card spot",
      explanation:
        "If the season ended now, they would make the playoffs without winning their division.",
    };
  }

  return {
    key: "hunt",
    label: "In the hunt",
    explanation:
      "They are outside the playoff spots right now, so every result matters.",
  };
}

function normalizeStandingEntry(entry, context) {
  const teamId = normalizeTeamId(entry?.team?.abbreviation);
  const team = getTeam(teamId);
  if (!team) return null;

  const stats = entry?.stats ?? [];
  const seed = Number(entry?.team?.seed);
  const rank = context.rank;
  const clincher = entry?.team?.clincher ?? null;
  const status = playoffStatusFor({
    seed: Number.isFinite(seed) ? seed : rank,
    clincher,
  });

  return {
    year: context.year,
    conferenceId: team.conference,
    conferenceName: context.conferenceName,
    divisionId: `${team.conference}-${team.division}`,
    divisionName: `${team.conference} ${team.division}`,
    rank,
    seed: Number.isFinite(seed) ? seed : rank,
    teamId,
    displayName: teamFullName(teamId),
    record: [statValue(stats, "wins"), statValue(stats, "losses"), statValue(stats, "ties")]
      .filter((value) => value != null)
      .join("-"),
    wins: numericStat(stats, "wins"),
    losses: numericStat(stats, "losses"),
    ties: numericStat(stats, "ties"),
    winPercent: statValue(stats, "winPercent") ?? statValue(stats, "winpercent"),
    pointDifferential: statValue(stats, "differential"),
    streak: statValue(stats, "streak"),
    clincher,
    clincherDescription: clincherDescription(context.payload, clincher),
    playoffStatus: status.key,
    playoffStatusLabel: status.label,
    explanation: status.explanation,
    source: "espn",
  };
}

export function normalizeSeasonStandings(payload, { year } = {}) {
  const conferences = (payload?.content?.standings?.groups ?? [])
    .map((group) => {
      const entries = group?.standings?.entries ?? [];
      const rankings = entries
        .map((entry, index) =>
          normalizeStandingEntry(entry, {
            year: Number(year ?? payload?.content?.params?.season) || null,
            rank: index + 1,
            conferenceName: group?.name ?? group?.abbreviation ?? "",
            payload,
          })
        )
        .filter(Boolean);

      return {
        conferenceId: group?.abbreviation ?? null,
        conferenceName: group?.name ?? group?.abbreviation ?? "",
        rankings,
        playoffSeeds: rankings
          .filter((team) => team.seed >= 1 && team.seed <= 7)
          .sort((a, b) => a.seed - b.seed),
      };
    })
    .filter((group) => group.rankings.length > 0);

  const rankings = conferences.flatMap((group) => group.rankings);
  const divisions = Object.values(
    rankings.reduce((acc, team) => {
      if (!acc[team.divisionId]) {
        acc[team.divisionId] = {
          divisionId: team.divisionId,
          divisionName: team.divisionName,
          conferenceId: team.conferenceId,
          teams: [],
        };
      }
      acc[team.divisionId].teams.push(team);
      return acc;
    }, {})
  ).map((division) => ({
    ...division,
    teams: division.teams.sort((a, b) => a.rank - b.rank),
  }));

  return {
    year: Number(year ?? payload?.content?.params?.season) || null,
    conferences,
    divisions,
    rankings,
    source: "espn",
  };
}

function bracketGameFrom(game, round) {
  const state = {
    status: game.status,
    homeTeamId: game.homeTeamId,
    awayTeamId: game.awayTeamId,
    homeScore: game.homeScore,
    awayScore: game.awayScore,
  };

  return {
    gameId: game.gameId,
    round: round.label,
    seasonType: game.seasonType,
    week: game.week,
    homeTeamId: game.homeTeamId,
    awayTeamId: game.awayTeamId,
    winnerTeamId: winnerTeamId(state),
    status: game.status,
    kickoffAt: game.kickoffAt,
    score:
      game.status === GAME_STATUS.FINAL || game.status === GAME_STATUS.IN_PROGRESS
        ? {
            home: game.homeScore,
            away: game.awayScore,
          }
        : null,
    nextGameHint: null,
  };
}

export function normalizeSeasonBracket(roundPayloads = [], { year } = {}) {
  const rounds = roundPayloads
    .filter((round) => BRACKET_ROUND_TEST.test(round?.label ?? ""))
    .map((round) => {
      const games = normalizeScheduleDaysFromPayload(round.payload)
        .map((game) => bracketGameFrom(game, round))
        .sort((a, b) => (a.kickoffAt ?? 0) - (b.kickoffAt ?? 0));

      return {
        key: String(round.label ?? round.week ?? "")
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, ""),
        label: round.label,
        week: round.week,
        games,
      };
    })
    .filter((round) => round.games.length > 0);

  return {
    year: Number(year) || null,
    rounds,
    source: "espn",
  };
}

function normalizeScheduleDaysFromPayload(payload) {
  const byDay = payload?.content?.schedule ?? {};

  return Object.keys(byDay)
    .sort()
    .flatMap((day) => byDay[day]?.games ?? [])
    .map(normalizeGame)
    .filter(Boolean);
}

function patchedTeamEvent(event, seasonType) {
  return {
    ...event,
    season: {
      ...event?.season,
      type: seasonType,
    },
  };
}

function teamGameResult(game, teamId) {
  if (game.status === GAME_STATUS.IN_PROGRESS) return "Live";
  if (game.status === GAME_STATUS.SCHEDULED) return "Upcoming";
  if (isOffSchedule(game.status)) return "Not played";
  if (game.status !== GAME_STATUS.FINAL) return "Unknown";

  const teamScore =
    game.homeTeamId === teamId ? game.homeScore : game.awayScore;
  const opponentScore =
    game.homeTeamId === teamId ? game.awayScore : game.homeScore;

  if (teamScore === opponentScore) return "Tie";
  return teamScore > opponentScore ? "Win" : "Loss";
}

function recapForTeamGame(game, teamId, opponentId) {
  if (game.status !== GAME_STATUS.FINAL) return null;

  const teamScore =
    game.homeTeamId === teamId ? game.homeScore : game.awayScore;
  const opponentScore =
    game.homeTeamId === teamId ? game.awayScore : game.homeScore;
  const margin = Math.abs(teamScore - opponentScore);
  const name = teamSpeakName(teamId);
  const opponent = teamSpeakName(opponentId);

  if (teamScore === opponentScore) {
    return `${name} tied ${opponent}. Neither side pulled away, so this one ended level.`;
  }

  const won = teamScore > opponentScore;
  const verb = teamVerb(teamId, won ? "won" : "lost", won ? "won" : "lost");
  const oneScore = margin <= 8;
  const shape = oneScore
    ? "It was a one-score game, so the ending stayed tense."
    : "That is more than one score, so the winner had some breathing room late.";

  return `${name} ${verb} by ${margin}. ${shape}`;
}

export function normalizeTeamSchedule(payload, { teamId, seasonType } = {}) {
  const id = String(teamId ?? "").trim().toUpperCase();
  const events = Array.isArray(payload?.events) ? payload.events : [];

  return events
    .map((event) => {
      const game = normalizeGame(patchedTeamEvent(event, seasonType));
      if (!game || (game.homeTeamId !== id && game.awayTeamId !== id)) {
        return null;
      }

      const isHome = game.homeTeamId === id;
      const opponentId = isHome ? game.awayTeamId : game.homeTeamId;
      const teamScore = isHome ? game.homeScore : game.awayScore;
      const opponentScore = isHome ? game.awayScore : game.homeScore;
      const result = teamGameResult(game, id);

      return {
        gameId: game.gameId,
        year: game.season,
        seasonType: game.seasonType,
        seasonTypeId: seasonType,
        week: game.week,
        roundLabel:
          event?.week?.text ??
          `${SEASON_TYPE_LABELS[seasonType] ?? "Season"}${game.week ? ` Week ${game.week}` : ""}`,
        teamId: id,
        opponentId,
        isHome,
        kickoffAt: game.kickoffAt,
        status: game.status,
        result,
        score:
          game.status === GAME_STATUS.FINAL || game.status === GAME_STATUS.IN_PROGRESS
            ? {
                team: teamScore,
                opponent: opponentScore,
                home: game.homeScore,
                away: game.awayScore,
              }
            : null,
        recap: recapForTeamGame(game, id, opponentId),
        keyMoment: null,
        playoffImpact: null,
        game,
        source: "espn",
      };
    })
    .filter(Boolean)
    .sort((a, b) => (a.kickoffAt ?? 0) - (b.kickoffAt ?? 0));
}

function scoringPlaysFrom(pkg) {
  return (pkg?.scoringPlays ?? []).map((play) => ({
    id: `score-${play.id}`,
    teamId: normalizeTeamId(play?.team?.abbreviation),
    text: play?.text ?? "",
    quarter: play?.period?.number ?? null,
    clock: play?.clock?.displayValue ?? null,
    homeScore: Number(play?.homeScore ?? 0),
    awayScore: Number(play?.awayScore ?? 0),
    type: play?.scoringType?.displayName ?? "Score",
  }));
}

function scoringByQuarterFrom(competition) {
  return (competition?.competitors ?? [])
    .map((competitor) => ({
      teamId: normalizeTeamId(competitor?.team?.abbreviation),
      total: Number(competitor?.score ?? 0),
      quarters: (competitor?.linescores ?? []).map((line, index) => ({
        quarter: index + 1,
        points: Number(line?.displayValue ?? line?.value ?? 0),
      })),
    }))
    .filter((row) => row.teamId);
}

function notablePlayersFrom(pkg) {
  return (pkg?.leaders ?? [])
    .flatMap((group) => group?.leaders ?? [])
    .map((category) => {
      const leader = category?.leaders?.[0];
      if (!leader?.athlete) return null;
      return {
        label: category.displayName ?? category.name ?? "Leader",
        playerId: leader.athlete.id ? `nfl-player-${leader.athlete.id}` : null,
        displayName: leader.athlete.displayName ?? leader.athlete.shortName ?? "",
        stat: leader.displayValue ?? leader.value ?? "",
        headshotUrl: leader.athlete.headshot?.href ?? null,
      };
    })
    .filter(Boolean)
    .slice(0, 4);
}

function recapSummary(game, state) {
  if (!game || state.status !== GAME_STATUS.FINAL) return null;

  const winner = winnerTeamId(state);
  if (!winner) {
    return `${teamFullName(game.awayTeamId)} and ${teamFullName(game.homeTeamId)} finished tied.`;
  }

  const loser = winner === game.homeTeamId ? game.awayTeamId : game.homeTeamId;
  const winnerScore = winner === game.homeTeamId ? game.homeScore : game.awayScore;
  const loserScore = winner === game.homeTeamId ? game.awayScore : game.homeScore;
  const margin = Math.abs(winnerScore - loserScore);

  return `${teamSpeakName(winner)} beat ${teamSpeakName(loser)} by ${margin}.`;
}

function whyItMattered(game, state) {
  if (!game || state.status !== GAME_STATUS.FINAL) return null;

  const total = Number(state.homeScore ?? 0) + Number(state.awayScore ?? 0);
  const margin = Math.abs(Number(state.homeScore ?? 0) - Number(state.awayScore ?? 0));

  if (margin <= 3) {
    return "A field goal could have changed the result, so late-game decisions mattered a lot.";
  }
  if (margin <= 8) {
    return "It stayed within one score, which means one touchdown drive could still flip the game late.";
  }
  if (total >= 55) {
    return "This was a high-scoring game, so both defenses spent a lot of time under pressure.";
  }
  return "The final margin was more than one score, so the winner had control by the end.";
}

export function normalizeGameRecap(pkg, { gameId } = {}) {
  const header = pkg?.header;
  const competition = header?.competitions?.[0];
  const event = competition
    ? {
        id: header.id,
        date: competition.date,
        season: header.season,
        week: { number: header.week },
        competitions: [competition],
      }
    : null;
  const game = event ? normalizeGame(event) : null;
  if (!game) return null;

  const state = {
    gameId: game.gameId,
    status: game.status,
    homeTeamId: game.homeTeamId,
    awayTeamId: game.awayTeamId,
    homeScore: game.homeScore,
    awayScore: game.awayScore,
  };

  const events = normalizeDrives(pkg);
  const conceptLabels = [
    ...new Set(
      events
        .filter((event) => event.knownType)
        .map((event) => labelForEvent(event.type))
        .filter(Boolean)
    ),
  ].slice(0, 5);

  return {
    gameId: game.gameId ?? gameId,
    status: game.status,
    finalScore:
      game.status === GAME_STATUS.FINAL
        ? {
            homeTeamId: game.homeTeamId,
            awayTeamId: game.awayTeamId,
            home: game.homeScore,
            away: game.awayScore,
          }
        : null,
    winnerTeamId: winnerTeamId(state),
    summary: recapSummary(game, state),
    whyItMattered: whyItMattered(game, state),
    keyMoments: scoringPlaysFrom(pkg).slice(-5).reverse(),
    scoringByQuarter: scoringByQuarterFrom(competition),
    notablePlayers: notablePlayersFrom(pkg),
    concepts: conceptLabels,
    source: "espn",
  };
}

export function normalizeTeamScheduleStatus(event) {
  return normalizeStatusType(event?.competitions?.[0]?.status?.type);
}
